import { UserRole } from "@features/settings/types/settings.types";
import Shimmer from "@shared/components/ui/Shimmer";
import { useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MetricsSection } from "../components";
import EditEventSectionModal from "../components/EventSection/EditEventSectionModal";
import EditGettingStartedModal from "../components/GettingStartedSection/EditGettingStartedModal";
import EditLeaderboardSectionModal from "../components/LeaderboardSection/EditLeaderboardSectionModal";
import EditQuoteSectionModal from "../components/QuoteSection/EditQuoteSectionModal";
import EditRecordingsSectionModal from "../components/RecordingsSection/EditRecordingsSectionModal";
import { SectionRenderer } from "../components/SectionRenderer";
import { useHomeStore } from "../store/home.store";
import {
  EventSectionData,
  LeaderboardSectionData,
  SectionConfig,
} from "../types/home.types";

const HomePage: React.FC = () => {
  // Get state and actions from home store
  const {
    homePageData,
    isLoading,
    error,
    fetchHomePageData,
    updateGettingStartedSection,
    updateEventSection,
    updateQuoteSection,
    updateRecordingsSection,
    updateLeaderboardSection,
  } = useHomeStore();

  // Get user profile to check admin status
  const { user } = useProfile();
  const isAdmin = user?.role === UserRole.ADMIN;

  // State for edit modals
  const [editModals, setEditModals] = useState({
    gettingStarted: false,
    event: false,
    quote: false,
    recordings: false,
    leaderboard: false,
  });

  // Memoized fetch function to avoid unnecessary re-renders
  const fetchData = useCallback(() => {
    fetchHomePageData();
  }, [fetchHomePageData]);

  // Fetch home page data on component mount only once
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Shimmer component for loading state
  const ShimmerBlock = ({ height = 400 }: { height?: number }) => (
    <Shimmer height={height} />
  );

  const eventData = {
    title: "",
    subtitle: "",
    description: "",
    buttonText: "",
    buttonLink: "",
    imagePath: "",
  };

  const quoteData = {
    quote:
      "We will never stop learning from others. Being connected and aware of what is happening around the world helps us to carry out better practices. Working together makes us stronger.",
    person: {
      name: "Francisco Freyria",
      role: "CEO/General Director",
      company: "Fundación Fomento de Desarrollo Teresa de Jesús IAP",
      imagePath: "/assets/images/home/Futeje_mug.jpg",
    },
  };

  const recordingsData = {
    title: "Watch Previous Meeting Recordings",
    recordings: [
      {
        month: "June",
        description: "Watch special guest lorem ipsum in date",
        buttonText: "Watch now",
        buttonLink: "#",
        buttonTarget: "_self",
        imagePath: "/assets/images/home/music.png",
      },
      {
        month: "May",
        description: "May meeting recording",
        buttonText: "Watch now",
        buttonLink: "#",
        buttonTarget: "_self",
      },
      {
        month: "April",
        description: "April meeting recording",
        buttonText: "Watch now",
        buttonLink: "#",
        buttonTarget: "_self",
      },
      {
        month: "March",
        description: "March meeting recording",
        buttonText: "Watch now",
        buttonLink: "#",
        buttonTarget: "_self",
      },
      {
        month: "February",
        description: "February meeting recording",
        buttonText: "Watch now",
        buttonLink: "#",
        buttonTarget: "_self",
      },
      {
        month: "January",
        description: "January meeting recording",
        buttonText: "Watch now",
        buttonLink: "#",
        buttonTarget: "_self",
      },
    ],
    viewAllText: "See all meeting replays",
    viewAllLink: "#",
  };

  // Default section configuration if none is provided from backend
  const defaultSectionConfig: SectionConfig[] = [
    {
      id: "gettingStarted",
      isVisible: true,
      order: 1,
      customClasses: "xl:col-span-1",
    },
    { id: "event", isVisible: true, order: 2, customClasses: "xl:col-span-2" },
    { id: "quote", isVisible: true, order: 3, customClasses: "xl:col-span-2" },
    {
      id: "recordings",
      isVisible: true,
      order: 4,
      customClasses: "xl:col-span-1",
    },
  ];

  // Get section configuration from backend or use default
  const sectionConfig = useMemo(() => {
    if (homePageData?.sectionConfig && homePageData.sectionConfig.length > 0) {
      return [...homePageData.sectionConfig].sort((a, b) => a.order - b.order);
    }
    return defaultSectionConfig;
  }, [homePageData?.sectionConfig]);

  // With the leaderboard on, it takes the recordings slot and the recordings
  // move to a full-width row right underneath it.
  const sections = useMemo<SectionConfig[]>(() => {
    if (!homePageData?.leaderboardSection?.enabled) return sectionConfig;

    const expanded = sectionConfig.flatMap<SectionConfig>((section) =>
      section.id === "recordings"
        ? [
            { ...section, id: "leaderboard" },
            {
              ...section,
              id: "recordings",
              customClasses: "md:col-span-2 xl:col-span-3",
            },
          ]
        : [section],
    );

    return expanded.map((section, index) => ({ ...section, order: index + 1 }));
  }, [sectionConfig, homePageData?.leaderboardSection?.enabled]);

  // Default data for sections
  const defaultData = {
    event: eventData,
    quote: quoteData,
    recordings: recordingsData,
  };

  // Generic handlers for edit modals
  const handleOpenEditModal = (section: keyof typeof editModals) => {
    setEditModals((prev) => ({ ...prev, [section]: true }));
  };

  const handleCloseEditModal = (section: keyof typeof editModals) => {
    setEditModals((prev) => ({ ...prev, [section]: false }));
  };

  // Section-specific submit handlers
  const handleEditGettingStartedSubmit = async (
    data: Partial<typeof homePageData.gettingStartedSection>,
  ) => {
    try {
      await updateGettingStartedSection(data);
      handleCloseEditModal("gettingStarted");
    } catch (error) {
      console.error("Error updating Getting Started section:", error);
      // Error is already handled in the store
    }
  };

  const handleEditEventSubmit = async (data: Partial<EventSectionData>) => {
    await updateEventSection(data);
  };

  const handleEditQuoteSubmit = async (
    data: Partial<typeof homePageData.quoteSection>,
  ) => {
    try {
      await updateQuoteSection(data);
      handleCloseEditModal("quote");
    } catch (error) {
      console.error("Error updating Quote section:", error);
      // Error is already handled in the store
    }
  };

  const handleEditRecordingsSubmit = async (
    data: Partial<typeof homePageData.recordingsSection>,
  ) => {
    try {
      await updateRecordingsSection(data);
      handleCloseEditModal("recordings");
    } catch (error) {
      console.error("Error updating Recordings section:", error);
      // Error is already handled in the store
    }
  };

  const handleEditLeaderboardSubmit = async (
    data: Partial<LeaderboardSectionData>,
  ) => {
    await updateLeaderboardSection(data);
    handleCloseEditModal("leaderboard");
  };

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Metrics Section */}
        <div className="mb-8">
          {isLoading ? (
            <ShimmerBlock height={200} />
          ) : (
            <MetricsSection
              metrics={homePageData?.metrics || []}
              user={homePageData?.user}
            />
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {/* Dynamically render sections based on configuration */}
          {sections
            .filter((section) => section.isVisible)
            .map((section) => (
              <div key={section.id} className={section.customClasses || ""}>
                <SectionRenderer
                  sectionId={section.id}
                  isLoading={isLoading}
                  isAdmin={isAdmin}
                  homePageData={homePageData}
                  defaultData={
                    defaultData[section.id as keyof typeof defaultData]
                  }
                  onEditClick={handleOpenEditModal}
                />
              </div>
            ))}
        </div>
      </div>
      {/* Edit Getting Started Modal */}
      {isAdmin && editModals.gettingStarted && (
        <EditGettingStartedModal
          isOpen={editModals.gettingStarted}
          onClose={() => handleCloseEditModal("gettingStarted")}
          onSubmit={handleEditGettingStartedSubmit}
          initialData={homePageData?.gettingStartedSection}
          isLoading={isLoading}
        />
      )}

      {/* Edit Event Modal */}
      {isAdmin && editModals.event && (
        <EditEventSectionModal
          isOpen={editModals.event}
          onClose={() => handleCloseEditModal("event")}
          onSubmit={handleEditEventSubmit}
          initialData={homePageData?.eventSection}
          isLoading={isLoading}
        />
      )}

      {/* Edit Quote Modal */}
      {isAdmin && editModals.quote && (
        <EditQuoteSectionModal
          isOpen={editModals.quote}
          onClose={() => handleCloseEditModal("quote")}
          onSubmit={handleEditQuoteSubmit}
          initialData={homePageData?.quoteSection || quoteData}
          isLoading={isLoading}
        />
      )}

      {/* Edit Recordings Modal */}
      {isAdmin && editModals.recordings && (
        <EditRecordingsSectionModal
          isOpen={editModals.recordings}
          onClose={() => handleCloseEditModal("recordings")}
          onSubmit={handleEditRecordingsSubmit}
          initialData={homePageData?.recordingsSection || recordingsData}
          isLoading={isLoading}
        />
      )}

      {/* Edit Leaderboard Modal */}
      {isAdmin && editModals.leaderboard && (
        <EditLeaderboardSectionModal
          isOpen={editModals.leaderboard}
          onClose={() => handleCloseEditModal("leaderboard")}
          onSubmit={handleEditLeaderboardSubmit}
          initialData={homePageData?.leaderboardSection}
        />
      )}
    </AuthenticatedLayout>
  );
};

export default HomePage;
